/**
 * Canonical slide-level classification annotation UI config.
 * Draft editing stays in ConfigForm; normalize at runtime and publish boundaries.
 */

import { normalizeCompletionPolicy } from '../../completionPolicy';
import {
    DEFAULT_ITEM_METADATA,
    normalizeItemMetadataConfig,
    prepareItemMetadataForAnnotationUISave,
    validateItemMetadataConfig,
} from '../../itemMetadata/itemMetadataModel';

/** Reserved category id for free-text answers (persisted + dropdown option when hybrid). */
export const SLIDE_LEVEL_FREE_TEXT_CATEGORY_ID = '__freeText__';

/** Reserved category id for explicit "none of the above" on multi-select questions. */
export const SLIDE_LEVEL_NONE_CATEGORY_ID = '__none__';

/** Default label for the injected None option on multi-select questions. */
export const DEFAULT_NONE_CATEGORY_LABEL = 'None of the above';

function toTrimmedString(v) {
    return typeof v === 'string' ? v.trim() : String(v ?? '').trim();
}

/**
 * @param {unknown} c
 * @param {number} index
 */
function normalizeOneCategory(c) {
    const idRaw = c?.id;
    const id = typeof idRaw === 'string' ? idRaw.trim() : String(idRaw ?? '').trim();
    const labelRaw = c?.label;
    const label = typeof labelRaw === 'string' ? labelRaw.trim() : String(labelRaw ?? '').trim();
    return {
        id,
        label: label || id,
    };
}

/** Default dropdown label for hybrid free-text option when config label is empty/whitespace-only. */
export const DEFAULT_FREE_TEXT_OPTION_LABEL = 'Other (free text)';

/** Default ViewerTopBar task section label for slide-level questions UI. */
export const DEFAULT_TASK_SECTION_LABEL = 'Questions:';

const EMPTY_ANSWER = {
    categoryId: null,
    categoryLabel: null,
    categoryIds: null,
    categoryLabels: null,
    freeText: null,
};

/**
 * Runtime / display label for the free-text dropdown option. Preserves admin draft spacing;
 * only whitespace-only strings fall back to {@link DEFAULT_FREE_TEXT_OPTION_LABEL}.
 *
 * @param {unknown} optionLabel - raw config `freeText.optionLabel` (draft-safe from normalize)
 */
export function getFreeTextOptionLabelForDisplay(optionLabel) {
    const s = optionLabel == null ? '' : String(optionLabel);
    return s.trim() === '' ? DEFAULT_FREE_TEXT_OPTION_LABEL : s;
}

/**
 * Trim free-text config strings at project save (placeholder, option label).
 * @param {object} question
 */
function trimQuestionFreeTextForPersist(question) {
    if (!question || typeof question !== 'object') return question;
    const ft = question.freeText;
    if (!ft || typeof ft !== 'object') return question;
    return {
        ...question,
        freeText: {
            ...ft,
            placeholder: toTrimmedString(ft.placeholder == null ? '' : String(ft.placeholder)),
            optionLabel: (() => {
                const t = toTrimmedString(ft.optionLabel == null ? '' : String(ft.optionLabel));
                return t || DEFAULT_FREE_TEXT_OPTION_LABEL;
            })(),
        },
    };
}

/**
 * Persisted config: questions[] only (no legacy top-level answer fields).
 * @param {object} config - normalized
 */
function stripCompatFieldsForPersist(config) {
    if (!config || typeof config !== 'object') return config;
    const questions = Array.isArray(config.questions)
        ? config.questions.map((q) => trimQuestionFreeTextForPersist(q))
        : [];
    const {
        categories: _c,
        allowDiscreteCategories: _d,
        allowFreeText: _f,
        freeText: _ft,
        ...rest
    } = config;
    return {
        ...rest,
        taskSectionLabel: toTrimmedString(rest.taskSectionLabel ?? DEFAULT_TASK_SECTION_LABEL)
            || DEFAULT_TASK_SECTION_LABEL,
        singleQuestionControlLabel: toTrimmedString(rest.singleQuestionControlLabel ?? ''),
        questions,
    };
}

/**
 * Apply canonical transforms to annotationUI immediately before PUT projects/:id.
 *
 * @param {{ id?: string, config?: object }} annotationUI
 */
export function prepareAnnotationUIForProjectSave(annotationUI) {
    const withMeta = prepareItemMetadataForAnnotationUISave(annotationUI);
    const ann = withMeta && typeof withMeta === 'object' ? withMeta : {};
    if (ann.id !== 'slide-level-classification') return withMeta;
    const cfg = ann.config && typeof ann.config === 'object' ? ann.config : {};
    const normalized = normalizeSlideLevelClassificationConfig(cfg);
    return {
        ...ann,
        config: stripCompatFieldsForPersist(normalized),
    };
}

/**
 * Whether the annotator has a complete answer for strict completion checks
 * (single-select / free-text / exclusive hybrid). Multi-select uses
 * {@link isQuestionAnswerComplete} instead.
 *
 * @param {object} params
 * @param {boolean} params.allowDiscreteCategories
 * @param {boolean} params.allowFreeText
 * @param {{ id: string }[]} params.categories
 * @param {string} params.categoryId
 * @param {string} params.freeTextAnswer
 */
export function isSlideLevelClassificationAnswerComplete({
    allowDiscreteCategories,
    allowFreeText,
    categories,
    categoryId,
    freeTextAnswer,
}) {
    const id = categoryId != null ? String(categoryId).trim() : '';
    const ftTrim = String(freeTextAnswer ?? '').trim();
    const ftPath = id === SLIDE_LEVEL_FREE_TEXT_CATEGORY_ID;

    if (allowDiscreteCategories && !allowFreeText) {
        return (
            id !== ''
            && id !== SLIDE_LEVEL_FREE_TEXT_CATEGORY_ID
            && categories.some((c) => String(c.id) === id)
        );
    }

    if (!allowDiscreteCategories && allowFreeText) {
        return ftTrim.length > 0;
    }

    if (allowDiscreteCategories && allowFreeText) {
        if (ftPath) return ftTrim.length > 0;
        return id !== '' && categories.some((c) => String(c.id) === id);
    }

    return false;
}

export const DEFAULT_SLIDE_LEVEL_QUESTION = {
    id: 'q1',
    label: '',
    categories: [],
    allowDiscreteCategories: true,
    allowFreeText: false,
    allowMultiple: false,
    freeText: {
        multiline: false,
        placeholder: '',
        optionLabel: DEFAULT_FREE_TEXT_OPTION_LABEL,
    },
};

export const DEFAULT_SLIDE_LEVEL_CLASSIFICATION_CONFIG = {
    instructions: '',
    taskSectionLabel: DEFAULT_TASK_SECTION_LABEL,
    singleQuestionControlLabel: '',
    questions: [{ ...DEFAULT_SLIDE_LEVEL_QUESTION }],
    itemMetadata: { ...DEFAULT_ITEM_METADATA },
    itemMetadataDisplayKeys: [],
    columnLabels: {},
};

/**
 * @param {unknown} q
 * @param {number} [index] - used when id is missing
 */
export function normalizeOneQuestion(q, index = 0) {
    const raw = q && typeof q === 'object' ? q : {};
    const idRaw = raw.id;
    let id = typeof idRaw === 'string' ? idRaw.trim() : String(idRaw ?? '').trim();
    if (!id) {
        id = `q${index + 1}`;
    }
    const labelRaw = raw.label;
    const label = labelRaw == null ? '' : String(labelRaw);
    const categoriesRaw = Array.isArray(raw.categories) ? raw.categories : [];
    const categories = categoriesRaw.map((c) => normalizeOneCategory(c));
    const modes = normalizeAnswerModes(raw);
    const allowMultiple = Boolean(raw.allowMultiple) && modes.allowDiscreteCategories;
    return {
        id,
        label,
        categories,
        allowDiscreteCategories: modes.allowDiscreteCategories,
        allowFreeText: modes.allowFreeText,
        allowMultiple,
        freeText: modes.freeText,
    };
}

/**
 * @param {string[]} [existingIds]
 */
export function createDefaultQuestion(existingIds = []) {
    const used = new Set(
        existingIds.map((id) => toTrimmedString(String(id))).filter(Boolean),
    );
    let n = 1;
    let id = `q${n}`;
    while (used.has(id)) {
        n += 1;
        id = `q${n}`;
    }
    return {
        ...DEFAULT_SLIDE_LEVEL_QUESTION,
        id,
        categories: [],
    };
}

/**
 * @param {object} raw - draft or persisted config
 * @returns {object[]}
 */
function migrateQuestionsFromRaw(raw) {
    const qRaw = Array.isArray(raw.questions) ? raw.questions : null;
    if (qRaw && qRaw.length > 0) {
        return qRaw.map((q, i) => normalizeOneQuestion(q, i));
    }
    const modes = normalizeAnswerModes(raw);
    const categoriesRaw = Array.isArray(raw.categories) ? raw.categories : [];
    return [
        normalizeOneQuestion({
            id: 'q1',
            label: '',
            categories: categoriesRaw,
            allowDiscreteCategories: modes.allowDiscreteCategories,
            allowFreeText: modes.allowFreeText,
            allowMultiple: Boolean(raw.allowMultiple) && modes.allowDiscreteCategories,
            freeText: modes.freeText,
        }, 0),
    ];
}

/**
 * Runtime compat: mirror first question onto top-level fields for single-question annotator UI.
 * @param {object} out - normalized config object to mutate
 * @param {object|undefined} primary
 */
function applyPrimaryQuestionCompat(out, primary) {
    if (!primary) return;
    out.categories = primary.categories;
    out.allowDiscreteCategories = primary.allowDiscreteCategories;
    out.allowFreeText = primary.allowFreeText;
    out.allowMultiple = primary.allowMultiple;
    out.freeText = primary.freeText;
}

/**
 * Categories shown to annotators for a question.
 * Multi: config categories, then Other (if free-text), then None.
 * @param {object} question - normalized
 * @returns {{ id: string, label: string }[]}
 */
export function getEffectiveCategoriesForAnnotator(question) {
    const categories = Array.isArray(question?.categories) ? question.categories : [];
    if (!question?.allowMultiple || !question?.allowDiscreteCategories) {
        return categories;
    }
    const out = [...categories];
    if (question.allowFreeText) {
        out.push({
            id: SLIDE_LEVEL_FREE_TEXT_CATEGORY_ID,
            label: getFreeTextOptionLabelForDisplay(question.freeText?.optionLabel),
        });
    }
    out.push({ id: SLIDE_LEVEL_NONE_CATEGORY_ID, label: DEFAULT_NONE_CATEGORY_LABEL });
    return out;
}

/**
 * Whether any question requires the QuestionsPanel (multi-question or any multi-select).
 * @param {object[]} questions
 */
export function shouldUseQuestionsPanel(questions) {
    if (!Array.isArray(questions) || questions.length === 0) return false;
    if (questions.length > 1) return true;
    return questions.some((q) => Boolean(q?.allowMultiple));
}

/**
 * Normalize id list from a stored or UI answer (migrates scalar categoryId).
 * Includes `__freeText__` when present (multi Other row).
 * @param {object|undefined} answer
 * @returns {string[]}
 */
export function getAnswerCategoryIds(answer) {
    if (!answer || typeof answer !== 'object') return [];
    if (Array.isArray(answer.categoryIds)) {
        return answer.categoryIds
            .map((id) => (id == null ? '' : String(id).trim()))
            .filter(Boolean);
    }
    const cid = answer.categoryId != null ? String(answer.categoryId).trim() : '';
    return cid ? [cid] : [];
}

/**
 * Parallel labels for {@link getAnswerCategoryIds}.
 * @param {object|undefined} answer
 * @param {string[]} [ids] - precomputed ids
 * @returns {string[]}
 */
export function getAnswerCategoryLabels(answer, ids) {
    const idList = ids ?? getAnswerCategoryIds(answer);
    if (!answer || typeof answer !== 'object') {
        return idList.map(() => '');
    }
    if (Array.isArray(answer.categoryLabels) && answer.categoryLabels.length === idList.length) {
        return answer.categoryLabels.map((l) => (l == null ? '' : String(l)));
    }
    if (idList.length === 1) {
        const cl = answer.categoryLabel != null ? String(answer.categoryLabel) : '';
        return [cl];
    }
    return idList.map(() => '');
}

/**
 * Toggle a category in a multi-select answer (None is exclusive).
 * @param {string[]} currentIds
 * @param {string[]} currentLabels
 * @param {string} toggleId
 * @param {string} toggleLabel
 * @returns {{ categoryIds: string[], categoryLabels: string[] }}
 */
export function toggleMultiCategorySelection(currentIds, currentLabels, toggleId, toggleLabel) {
    const id = String(toggleId ?? '').trim();
    const label = toggleLabel != null ? String(toggleLabel) : id;
    const ids = Array.isArray(currentIds) ? [...currentIds] : [];
    const labels = Array.isArray(currentLabels) ? [...currentLabels] : [];

    if (!id) {
        return { categoryIds: [], categoryLabels: [] };
    }

    if (id === SLIDE_LEVEL_NONE_CATEGORY_ID) {
        const hasNone = ids.includes(SLIDE_LEVEL_NONE_CATEGORY_ID);
        if (hasNone) {
            return { categoryIds: [], categoryLabels: [] };
        }
        return {
            categoryIds: [SLIDE_LEVEL_NONE_CATEGORY_ID],
            categoryLabels: [label || DEFAULT_NONE_CATEGORY_LABEL],
        };
    }

    const withoutNoneIds = [];
    const withoutNoneLabels = [];
    ids.forEach((existingId, i) => {
        if (existingId === SLIDE_LEVEL_NONE_CATEGORY_ID) return;
        withoutNoneIds.push(existingId);
        withoutNoneLabels.push(labels[i] ?? '');
    });

    const idx = withoutNoneIds.indexOf(id);
    if (idx >= 0) {
        withoutNoneIds.splice(idx, 1);
        withoutNoneLabels.splice(idx, 1);
    } else {
        withoutNoneIds.push(id);
        withoutNoneLabels.push(label);
    }
    return { categoryIds: withoutNoneIds, categoryLabels: withoutNoneLabels };
}

/**
 * @param {object} [config]
 * @returns {object[]}
 */
export function getSlideLevelClassificationQuestions(config) {
    return normalizeSlideLevelClassificationConfig(config).questions;
}

/**
 * Display name for a question in viewer UI and validation messages.
 * @param {object} q
 * @param {number} qIndex
 */
export function getQuestionDisplayName(q, qIndex) {
    const label = toTrimmedString(q?.label ?? '');
    if (label) return label;
    return `Question ${qIndex + 1}`;
}

/**
 * Strip trailing colon for panel trigger prefix (e.g. "Questions:" → "Questions").
 * @param {string} taskSectionLabel
 */
export function getTaskSectionLabelPrefix(taskSectionLabel) {
    const s = toTrimmedString(taskSectionLabel ?? DEFAULT_TASK_SECTION_LABEL);
    return s.replace(/:+\s*$/, '') || 'Questions';
}

/**
 * ViewerTopBar task section label: config → registry fallback → default.
 * @param {object} [uiConfig]
 * @param {string} [registryLabel]
 */
export function getSlcTaskSectionLabel(uiConfig, registryLabel) {
    const normalized = normalizeSlideLevelClassificationConfig(uiConfig);
    const fromConfig = toTrimmedString(normalized.taskSectionLabel);
    if (fromConfig) return fromConfig;
    const fromRegistry = toTrimmedString(registryLabel ?? '');
    if (fromRegistry) return fromRegistry;
    return DEFAULT_TASK_SECTION_LABEL;
}

/**
 * @param {unknown} raw
 * @returns {{
 *   categoryId: string|null,
 *   categoryLabel: string|null,
 *   categoryIds: string[]|null,
 *   categoryLabels: string[]|null,
 *   freeText: string|null,
 * }}
 */
export function normalizeStoredAnswer(raw) {
    if (!raw || typeof raw !== 'object') {
        return { ...EMPTY_ANSWER };
    }
    const ft = raw.freeText;
    const freeText = ft == null || String(ft).trim() === '' ? null : String(ft).trim();

    let categoryIds = null;
    let categoryLabels = null;
    if (Array.isArray(raw.categoryIds)) {
        categoryIds = raw.categoryIds
            .map((id) => (id == null ? '' : String(id).trim()))
            .filter(Boolean);
        if (Array.isArray(raw.categoryLabels)) {
            // Align labels to filtered ids by original index before filter.
            const rawIds = raw.categoryIds.map((id) => (id == null ? '' : String(id).trim()));
            categoryLabels = categoryIds.map((id) => {
                const idx = rawIds.indexOf(id);
                const l = idx >= 0 ? raw.categoryLabels[idx] : '';
                return l == null ? '' : String(l);
            });
        } else {
            categoryLabels = categoryIds.map((id) => (
                id === SLIDE_LEVEL_NONE_CATEGORY_ID ? DEFAULT_NONE_CATEGORY_LABEL : ''
            ));
        }
        if (categoryIds.length === 0) {
            categoryIds = null;
            categoryLabels = null;
        }
    }

    let categoryId = null;
    let categoryLabel = null;
    if (categoryIds && categoryIds.length > 0) {
        categoryId = categoryIds[0];
        categoryLabel = (categoryLabels?.[0] && String(categoryLabels[0]).trim())
            ? String(categoryLabels[0])
            : null;
    } else {
        const cid = raw.categoryId;
        categoryId = cid == null || String(cid).trim() === '' ? null : String(cid).trim();
        const cl = raw.categoryLabel;
        categoryLabel = cl == null || String(cl).trim() === '' ? null : String(cl);
        if (categoryId) {
            categoryIds = [categoryId];
            categoryLabels = [categoryLabel || ''];
        }
    }

    return {
        categoryId,
        categoryLabel,
        categoryIds,
        categoryLabels,
        freeText,
    };
}

/**
 * Ensure legacy multi answers with freeText but no Other id get `__freeText__` appended.
 * @param {object} question - normalized
 * @param {object} answer - normalized stored answer
 */
export function ensureMultiOtherIdForLegacyFreeText(question, answer) {
    const a = answer && typeof answer === 'object' ? { ...answer } : { ...EMPTY_ANSWER };
    if (!question?.allowMultiple || !question?.allowDiscreteCategories || !question?.allowFreeText) {
        return a;
    }
    const ft = a.freeText != null ? String(a.freeText).trim() : '';
    if (!ft) return a;
    const ids = getAnswerCategoryIds(a);
    if (ids.includes(SLIDE_LEVEL_FREE_TEXT_CATEGORY_ID)) return a;
    const labels = getAnswerCategoryLabels(a, ids);
    const ftLabel = getFreeTextOptionLabelForDisplay(question.freeText?.optionLabel);
    // Drop None if present — Other + notes implies not "none of the above".
    const nextIds = ids.filter((id) => id !== SLIDE_LEVEL_NONE_CATEGORY_ID);
    const nextLabels = ids
        .map((id, i) => ({ id, label: labels[i] ?? '' }))
        .filter(({ id }) => id !== SLIDE_LEVEL_NONE_CATEGORY_ID)
        .map(({ label }) => label);
    nextIds.push(SLIDE_LEVEL_FREE_TEXT_CATEGORY_ID);
    nextLabels.push(ftLabel);
    return {
        ...a,
        categoryIds: nextIds,
        categoryLabels: nextLabels,
        categoryId: nextIds[0] ?? null,
        categoryLabel: nextLabels[0] || null,
        freeText: ft,
    };
}

/**
 * Load answers map from exportData, migrating legacy flat slideClassification when needed.
 * @param {object|undefined} exportData
 * @param {object[]} questions - normalized questions
 */
export function migrateAnswersFromExportData(exportData, questions) {
    const sc = exportData?.slideClassification;
    const out = {};
    questions.forEach((q) => {
        out[q.id] = { ...EMPTY_ANSWER };
    });

    if (!sc || typeof sc !== 'object') {
        return out;
    }

    const answersRaw = sc.answers;
    if (answersRaw && typeof answersRaw === 'object' && !Array.isArray(answersRaw)) {
        questions.forEach((q) => {
            if (Object.prototype.hasOwnProperty.call(answersRaw, q.id)) {
                out[q.id] = ensureMultiOtherIdForLegacyFreeText(
                    q,
                    normalizeStoredAnswer(answersRaw[q.id]),
                );
            }
        });
        return out;
    }

    const primaryId = questions[0]?.id ?? 'q1';
    if (
        sc.categoryId != null
        || sc.categoryLabel != null
        || (sc.freeText != null && String(sc.freeText).trim() !== '')
    ) {
        const primaryQ = questions[0];
        out[primaryId] = ensureMultiOtherIdForLegacyFreeText(
            primaryQ,
            normalizeStoredAnswer(sc),
        );
    }
    return out;
}

/**
 * Build one answer entry for persist.
 * @param {object} params
 */
export function buildStoredAnswerPayload({
    allowDiscreteCategories,
    allowFreeText,
    allowMultiple = false,
    categoryId,
    categoryLabel,
    categoryIds,
    categoryLabels,
    freeTextAnswer,
    freeTextOptionLabel,
}) {
    const ftTrim = String(freeTextAnswer ?? '').trim();

    if (allowMultiple && allowDiscreteCategories) {
        let ids = (Array.isArray(categoryIds) ? categoryIds : getAnswerCategoryIds({
            categoryId,
            categoryIds,
        }))
            .map((id) => String(id).trim())
            .filter(Boolean);
        if (!allowFreeText) {
            ids = ids.filter((id) => id !== SLIDE_LEVEL_FREE_TEXT_CATEGORY_ID);
        }
        let labels = Array.isArray(categoryLabels)
            ? categoryLabels.map((l) => (l == null ? '' : String(l)))
            : [];
        if (labels.length !== ids.length) {
            labels = ids.map((id, i) => {
                if (id === SLIDE_LEVEL_NONE_CATEGORY_ID) return DEFAULT_NONE_CATEGORY_LABEL;
                if (id === SLIDE_LEVEL_FREE_TEXT_CATEGORY_ID) {
                    return freeTextOptionLabel || DEFAULT_FREE_TEXT_OPTION_LABEL;
                }
                if (Array.isArray(categoryLabels) && categoryLabels[i] != null) {
                    return String(categoryLabels[i]);
                }
                return '';
            });
        }
        const hasOther = ids.includes(SLIDE_LEVEL_FREE_TEXT_CATEGORY_ID);
        const freeText = allowFreeText && hasOther && ftTrim ? ftTrim : null;
        if (ids.length === 0) {
            return { ...EMPTY_ANSWER };
        }
        return {
            categoryIds: ids,
            categoryLabels: labels,
            categoryId: ids[0] ?? null,
            categoryLabel: (labels[0] && String(labels[0]).trim()) ? labels[0] : null,
            freeText,
        };
    }

    const id = categoryId != null ? String(categoryId).trim() : '';
    const label = categoryLabel != null ? String(categoryLabel) : '';

    if (!allowDiscreteCategories && allowFreeText) {
        if (!ftTrim) return { ...EMPTY_ANSWER };
        return {
            categoryId: SLIDE_LEVEL_FREE_TEXT_CATEGORY_ID,
            categoryLabel: freeTextOptionLabel || null,
            categoryIds: null,
            categoryLabels: null,
            freeText: ftTrim,
        };
    }

    if (id === SLIDE_LEVEL_FREE_TEXT_CATEGORY_ID) {
        const has = ftTrim.length > 0;
        return {
            categoryId: has ? SLIDE_LEVEL_FREE_TEXT_CATEGORY_ID : null,
            categoryLabel: has ? (label || freeTextOptionLabel || null) : null,
            categoryIds: null,
            categoryLabels: null,
            freeText: has ? ftTrim : null,
        };
    }

    const hasDiscrete = id !== '' && id !== SLIDE_LEVEL_FREE_TEXT_CATEGORY_ID;
    return {
        categoryId: hasDiscrete ? id : null,
        categoryLabel: hasDiscrete ? (label || null) : null,
        categoryIds: null,
        categoryLabels: null,
        freeText: null,
    };
}

/**
 * Whether exportData has any answered question.
 * @param {Record<string, { categoryId?: *, categoryIds?: *, freeText?: * }>} answers
 */
export function hasAnySlideClassificationAnswer(answers) {
    return Object.values(answers || {}).some((a) => {
        const ids = getAnswerCategoryIds(a);
        const cid = a?.categoryId != null ? String(a.categoryId).trim() : '';
        const ft = a?.freeText != null ? String(a.freeText).trim() : '';
        return ids.length > 0 || Boolean(cid) || Boolean(ft);
    });
}

/**
 * @param {object} question - normalized question
 * @param {{ categoryId?: string|null, categoryIds?: string[]|null, categoryLabel?: string|null, freeText?: string|null }} answer
 */
export function isQuestionAnswerComplete(question, answer) {
    const a = answer ?? EMPTY_ANSWER;
    if (question?.allowMultiple && question?.allowDiscreteCategories) {
        const ids = getAnswerCategoryIds(a);
        if (ids.length === 0) return false;
        const effectiveIds = new Set(
            getEffectiveCategoriesForAnnotator(question).map((c) => String(c.id)),
        );
        const ftTrim = String(a.freeText ?? '').trim();
        if (ids.includes(SLIDE_LEVEL_FREE_TEXT_CATEGORY_ID) && ftTrim.length === 0) {
            return false;
        }
        return ids.every((id) => effectiveIds.has(id));
    }
    return isSlideLevelClassificationAnswerComplete({
        allowDiscreteCategories: question.allowDiscreteCategories,
        allowFreeText: question.allowFreeText,
        categories: question.categories ?? [],
        categoryId: a.categoryId ?? '',
        freeTextAnswer: a.freeText ?? '',
    });
}

/**
 * @param {object[]} questions - normalized
 * @param {Record<string, object>} answers
 */
export function isSlideLevelClassificationSlideComplete(questions, answers) {
    if (!Array.isArray(questions) || questions.length === 0) return false;
    return questions.every((q) => isQuestionAnswerComplete(q, answers?.[q.id]));
}

/**
 * @param {object[]} questions
 * @param {Record<string, object>} answers
 * @returns {string[]}
 */
export function getUnansweredQuestionLabels(questions, answers) {
    const labels = [];
    questions.forEach((q, i) => {
        if (!isQuestionAnswerComplete(q, answers?.[q.id])) {
            labels.push(getQuestionDisplayName(q, i));
        }
    });
    return labels;
}

/**
 * Count answered questions.
 * @param {object[]} questions
 * @param {Record<string, object>} answers
 */
export function countAnsweredQuestions(questions, answers) {
    let n = 0;
    questions.forEach((q) => {
        if (isQuestionAnswerComplete(q, answers?.[q.id])) n += 1;
    });
    return n;
}

/** Max selectable rows (categories + hybrid free-text) before using a dropdown control. */
export const SLC_MAX_INLINE_CHOICES = 5;

/**
 * Count of selectable answer rows (excludes Clear); hybrid includes free-text option;
 * multi-select includes injected None.
 * @param {object} question - normalized question
 */
export function getQuestionSelectableChoiceCount(question) {
    if (!question?.allowDiscreteCategories) return 0;
    if (question.allowMultiple) {
        return getEffectiveCategoriesForAnnotator(question).length;
    }
    const categories = Array.isArray(question.categories) ? question.categories : [];
    let n = categories.length;
    if (question.allowDiscreteCategories && question.allowFreeText) {
        n += 1;
    }
    return n;
}

/**
 * @param {object} question - normalized question
 */
/**
 * Inline choice list is for QuestionsPanel cards only; TopBarControls passes layout="topBar".
 */
export function shouldUseInlineChoices(question) {
    if (!question?.allowDiscreteCategories) return false;
    return getQuestionSelectableChoiceCount(question) <= SLC_MAX_INLINE_CHOICES;
}

/**
 * First unanswered question index, or 0 when all complete (callers that show UI
 * for the featured question should treat answeredCount === total separately).
 * @param {object[]} questions
 * @param {Record<string, object>} answers
 */
export function getFeaturedQuestionIndex(questions, answers) {
    if (!Array.isArray(questions) || questions.length === 0) return 0;
    const idx = questions.findIndex(
        (q) => !isQuestionAnswerComplete(q, answers?.[q.id]),
    );
    return idx >= 0 ? idx : 0;
}

/**
 * Short summary of current answer for collapsed strip when using dropdown mode.
 * @param {object} question
 * @param {{ categoryId?: string, categoryIds?: string[], categoryLabel?: string, categoryLabels?: string[], freeText?: string }|undefined} answer
 * @param {number} [qIndex]
 */
export function formatAnswerSummary(question, answer, qIndex = 0) {
    const a = answer ?? {};
    if (!isQuestionAnswerComplete(question, a)) {
        return 'Not answered';
    }
    if (question?.allowMultiple && question?.allowDiscreteCategories) {
        const ids = getAnswerCategoryIds(a);
        const labels = getAnswerCategoryLabels(a, ids);
        const effective = getEffectiveCategoriesForAnnotator(question);
        const parts = [];
        ids.forEach((id, i) => {
            if (id === SLIDE_LEVEL_FREE_TEXT_CATEGORY_ID) {
                const ft = String(a.freeText ?? '').trim();
                if (ft) {
                    parts.push(ft.length > 40 ? `${ft.slice(0, 39)}…` : ft);
                } else {
                    parts.push(getFreeTextOptionLabelForDisplay(question.freeText?.optionLabel));
                }
                return;
            }
            const fromAns = String(labels[i] ?? '').trim();
            if (fromAns) {
                parts.push(fromAns);
                return;
            }
            const cat = effective.find((c) => String(c.id) === id);
            if (cat) {
                parts.push(cat.label?.trim() ? cat.label : String(cat.id));
                return;
            }
            parts.push(id);
        });
        const joined = parts.join(', ');
        return joined.length > 48 ? `${joined.slice(0, 47)}…` : joined;
    }
    const id = a.categoryId != null ? String(a.categoryId).trim() : '';
    if (id === SLIDE_LEVEL_FREE_TEXT_CATEGORY_ID) {
        const ft = String(a.freeText ?? '').trim();
        if (ft) return ft.length > 48 ? `${ft.slice(0, 47)}…` : ft;
        return getFreeTextOptionLabelForDisplay(question.freeText?.optionLabel);
    }
    const cl = String(a.categoryLabel ?? '').trim();
    if (cl) return cl;
    const cat = (question.categories ?? []).find((c) => String(c.id) === id);
    if (cat) return cat.label?.trim() ? cat.label : String(cat.id);
    return getQuestionDisplayName(question, qIndex);
}

/**
 * Normalizes free-text config for in-memory / form use. Do not trim optionLabel here — ConfigForm
 * reads normalized config as controlled values; trimming breaks typing (UI_CONFIG_MODEL_PRINCIPLES.md).
 * Trim only in {@link trimFreeTextConfigForPersist}.
 *
 * @param {unknown} ft
 * @returns {{ multiline: boolean, placeholder: string, optionLabel: string }}
 */
export function normalizeFreeTextSettings(ft) {
    const o = ft && typeof ft === 'object' ? ft : {};
    return {
        multiline: Boolean(o.multiline),
        placeholder: o.placeholder == null ? '' : String(o.placeholder),
        optionLabel: o.optionLabel == null ? '' : String(o.optionLabel),
    };
}

/**
 * Legacy configs omit allow* flags → discrete categories only.
 * @param {object} raw
 */
function normalizeAnswerModes(raw) {
    const hasD = Object.prototype.hasOwnProperty.call(raw, 'allowDiscreteCategories');
    const hasF = Object.prototype.hasOwnProperty.call(raw, 'allowFreeText');
    if (!hasD && !hasF) {
        return {
            allowDiscreteCategories: true,
            allowFreeText: false,
            freeText: normalizeFreeTextSettings({}),
        };
    }
    return {
        allowDiscreteCategories: raw.allowDiscreteCategories !== false,
        allowFreeText: Boolean(raw.allowFreeText),
        freeText: normalizeFreeTextSettings(raw.freeText),
    };
}

/**
 * Safe annotation UI config for runtime + publish validation.
 * @param {object} [config]
 */
export function normalizeSlideLevelClassificationConfig(config) {
    const raw = config && typeof config === 'object' ? config : {};

    const questions = migrateQuestionsFromRaw(raw);
    const meta = normalizeItemMetadataConfig(raw);

    const taskSectionLabelRaw = raw.taskSectionLabel;
    const taskSectionLabel = taskSectionLabelRaw == null
        ? DEFAULT_TASK_SECTION_LABEL
        : String(taskSectionLabelRaw);
    const sqLabelRaw = raw.singleQuestionControlLabel;
    const singleQuestionControlLabel = sqLabelRaw == null ? '' : String(sqLabelRaw);

    const out = {
        ...DEFAULT_SLIDE_LEVEL_CLASSIFICATION_CONFIG,
        ...raw,
        instructions: typeof raw.instructions === 'string' ? raw.instructions : '',
        taskSectionLabel,
        singleQuestionControlLabel,
        questions,
        itemMetadata: meta.itemMetadata,
        itemMetadataDisplayKeys: meta.itemMetadataDisplayKeys,
        columnLabels: meta.columnLabels,
        completionPolicy: normalizeCompletionPolicy(raw),
    };

    if (raw.roi && typeof raw.roi === 'object') {
        out.roi = raw.roi;
    }

    applyPrimaryQuestionCompat(out, questions[0]);

    return out;
}

/**
 * @param {object} config - should be normalized
 * @returns {{ code: string, message: string }[]}
 */
export function validateSlideLevelClassificationConfig(config) {
    const c = normalizeSlideLevelClassificationConfig(config);
    const issues = [];

    const questions = Array.isArray(c.questions) ? c.questions : [];

    if (questions.length < 1) {
        issues.push({
            code: 'no_questions',
            message: 'Add at least one question.',
        });
    }

    const im = c.itemMetadata ?? {};
    const rows = Array.isArray(im.rows) ? im.rows : [];
    const matchOpts = {
        trimWhitespace: im.trimWhitespace !== false,
        caseInsensitive: Boolean(im.caseInsensitive),
    };

    issues.push(...validateItemMetadataConfig(c));

    const seenQuestionIds = new Set();
    let anyDiscreteWithoutCategories = false;

    questions.forEach((q, qIdx) => {
        const qName = getQuestionDisplayName(q, qIdx);
        const qid = String(q.id ?? '').trim();

        if (!qid) {
            issues.push({
                code: 'empty_question_id',
                message: `${qName}: question id is required.`,
            });
        } else if (seenQuestionIds.has(qid)) {
            issues.push({
                code: 'duplicate_question_id',
                message: `${qName}: duplicate question id "${qid}".`,
            });
        } else {
            seenQuestionIds.add(qid);
        }

        const allowDisc = Boolean(q.allowDiscreteCategories);
        const allowFt = Boolean(q.allowFreeText);
        const allowMultiple = Boolean(q.allowMultiple);

        if (!allowDisc && !allowFt) {
            issues.push({
                code: 'no_answer_mode',
                message: `${qName}: choose at least one answer type (discrete categories and/or free-text).`,
            });
        }

        if (allowMultiple && !allowDisc) {
            issues.push({
                code: 'allow_multiple_requires_discrete',
                message: `${qName}: choose-all-that-apply requires discrete categories.`,
            });
        }

        const categories = Array.isArray(q.categories) ? q.categories : [];

        if (allowDisc && categories.length === 0) {
            issues.push({
                code: 'missing_categories',
                message: `${qName}: discrete categories are enabled — add at least one category.`,
            });
            anyDiscreteWithoutCategories = true;
        }

        const seenCatIds = new Set();
        categories.forEach((cat, i) => {
            const rowNum = i + 1;
            const id = String(cat.id ?? '').trim();
            if (!id) {
                if (allowDisc) {
                    issues.push({
                        code: 'empty_category_id',
                        message: `${qName}, category row ${rowNum}: id is required.`,
                    });
                }
                return;
            }
            if (id === SLIDE_LEVEL_FREE_TEXT_CATEGORY_ID) {
                issues.push({
                    code: 'reserved_free_text_category_id',
                    message: `${qName}, category row ${rowNum}: id "${SLIDE_LEVEL_FREE_TEXT_CATEGORY_ID}" is reserved for free-text answers.`,
                });
                return;
            }
            if (id === SLIDE_LEVEL_NONE_CATEGORY_ID) {
                issues.push({
                    code: 'reserved_none_category_id',
                    message: `${qName}, category row ${rowNum}: id "${SLIDE_LEVEL_NONE_CATEGORY_ID}" is reserved for "none of the above" on multi-select questions.`,
                });
                return;
            }
            if (seenCatIds.has(id)) {
                issues.push({
                    code: 'duplicate_category_id',
                    message: `${qName}, category row ${rowNum}: duplicate id "${id}".`,
                });
            }
            seenCatIds.add(id);
        });
    });

    if (rows.length > 0 && anyDiscreteWithoutCategories) {
        issues.push({
            code: 'slide_rows_without_categories',
            message: 'Item metadata rows are configured but at least one question has discrete categories enabled with no categories defined.',
        });
    }

    return issues;
}

const PUBLISH_BLOCKING_CODES = new Set([
    'no_questions',
    'no_answer_mode',
    'empty_question_id',
    'duplicate_question_id',
    'empty_category_id',
    'duplicate_category_id',
    'reserved_free_text_category_id',
    'reserved_none_category_id',
    'allow_multiple_requires_discrete',
]);

/**
 * Reasons that should disable project Publish while in Draft.
 * @param {object} config - annotationUI.config (draft)
 * @returns {string[]}
 */
export function getSlideLevelClassificationPublishBlockingReasons(config) {
    const issues = validateSlideLevelClassificationConfig(config);
    return issues
        .filter((i) => PUBLISH_BLOCKING_CODES.has(i.code))
        .map((i) => i.message);
}
